import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_ENDPOINTS, apiClient } from '../config/api';
import { auth } from '../firebase/config';
import { getActiveRole } from '../utils/getActiveRole';
import SharePropertyModal from '../components/property/SharePropertyModal';
import AddressAutocomplete from '../components/common/AddressAutocomplete';
import PropertyFiltersModal, {
  DEFAULT_FILTERS,
  filtersToSearchParams,
  countActiveFilters,
} from '../components/property/PropertyFiltersModal';
import './PropertiesPage.css';

// US state abbreviation → full name — matches mobile toFullStateName()
const STATE_NAMES = {
  AL:'Alabama', AK:'Alaska', AZ:'Arizona', AR:'Arkansas', CA:'California',
  CO:'Colorado', CT:'Connecticut', DE:'Delaware', FL:'Florida', GA:'Georgia',
  HI:'Hawaii', ID:'Idaho', IL:'Illinois', IN:'Indiana', IA:'Iowa',
  KS:'Kansas', KY:'Kentucky', LA:'Louisiana', ME:'Maine', MD:'Maryland',
  MA:'Massachusetts', MI:'Michigan', MN:'Minnesota', MS:'Mississippi', MO:'Missouri',
  MT:'Montana', NE:'Nebraska', NV:'Nevada', NH:'New Hampshire', NJ:'New Jersey',
  NM:'New Mexico', NY:'New York', NC:'North Carolina', ND:'North Dakota', OH:'Ohio',
  OK:'Oklahoma', OR:'Oregon', PA:'Pennsylvania', RI:'Rhode Island', SC:'South Carolina',
  SD:'South Dakota', TN:'Tennessee', TX:'Texas', UT:'Utah', VT:'Vermont',
  VA:'Virginia', WA:'Washington', WV:'West Virginia', WI:'Wisconsin', WY:'Wyoming',
  DC:'District of Columbia',
};
const toFullStateName = (abbr) => STATE_NAMES[abbr?.toUpperCase()] || abbr || '';
const isUSStateAbbr = (val) => val?.length === 2 && !!STATE_NAMES[val.toUpperCase()];
const FULL_STATE_NAME_TO_ABBR = Object.fromEntries(
  Object.entries(STATE_NAMES).map(([abbr, name]) => [name.toLowerCase(), abbr])
);

// Parses a free-typed full address string (e.g. "21809 Martinez St, Woodland Hills, CA 91364")
// into street/city/state/zip components. Mirrors mobile's SearchPropertyScreen parseFreeAddress()
// so that typing (rather than selecting from the autocomplete dropdown) still searches by street.
const parseFreeAddress = (raw) => {
  const result = {};
  if (!raw || !raw.trim()) return result;

  let working = raw.trim();

  const zipMatch = working.match(/(\d{5})(?:-\d{4})?\s*$/);
  if (zipMatch) {
    result.zip = zipMatch[1];
    working = working.slice(0, zipMatch.index).trim().replace(/,\s*$/, '').trim();
  }

  const stateAbbrMatch = working.match(/[\s,]+([A-Za-z]{2})\s*$/);
  if (stateAbbrMatch && isUSStateAbbr(stateAbbrMatch[1])) {
    result.stateAbbr = stateAbbrMatch[1].toUpperCase();
    working = working.slice(0, stateAbbrMatch.index).trim().replace(/,\s*$/, '').trim();
  } else {
    const stateNameMatch = working.match(/[\s,]+([A-Za-z ]+?)\s*$/);
    if (stateNameMatch) {
      const abbr = FULL_STATE_NAME_TO_ABBR[stateNameMatch[1].trim().toLowerCase()];
      if (abbr) {
        result.stateAbbr = abbr;
        working = working.slice(0, stateNameMatch.index).trim().replace(/,\s*$/, '').trim();
      }
    }
  }

  const lastComma = working.lastIndexOf(',');
  let street;
  if (lastComma >= 0) {
    street = working.slice(0, lastComma).trim();
    result.city = working.slice(lastComma + 1).trim();
  } else if (/\d/.test(working)) {
    street = working;
  } else {
    result.city = working;
  }

  if (street) {
    // Drop unit/apt/suite tokens — providers store these differently, so including
    // them in a substring match makes the address miss listings that otherwise match.
    street = street
      .replace(/\s*#\s*\S+\s*$/i, '')
      .replace(/\s*(?:unit|apt|apartment|suite|ste)\s+\S+\s*$/i, '')
      .trim();
    result.street = street;
  }
  return result;
};

/**
 * Transform raw MLS nested API response to flat UI-friendly format.
 * Mirrors mobile app's property.service.ts transformMLSProperty().
 * API returns: { mlsId, listPrice, address: { city, state, ... }, property: { bedrooms, ... }, mls: { status, daysOnMarket } }
 */
const transformMLSProperty = (mls) => {
  if (!mls || typeof mls !== 'object') return null;

  const address = mls.address || {};
  const property = mls.property || {};
  const mlsInfo = mls.mls || {};

  // Determine status badge (same logic as mobile)
  let status = null;
  if (mlsInfo.daysOnMarket !== undefined && mlsInfo.daysOnMarket <= 3) {
    status = 'NEW';
  } else if (mlsInfo.status?.toLowerCase() === 'pending') {
    status = 'PENDING';
  } else if (mlsInfo.status?.toLowerCase() === 'active') {
    status = 'ACTIVE';
  }

  // Calculate total baths: full + half*0.5 + three-quarter*0.75
  const bathsFull = property.bathsFull || 0;
  const bathsHalf = property.bathsHalf || 0;
  const bathsThreeQuarter = property.bathsThreeQuarter || 0;
  const totalBaths = bathsFull + (bathsHalf * 0.5) + (bathsThreeQuarter * 0.75);

  const assoc = mls.association || property.association || {};

  return {
    id: String(mls.mlsId || mls.listingId || ''),
    mlsId: mls.mlsId,
    price: mls.listPrice || 0,
    address: address.full ||
      [address.streetNumber, address.streetName, address.streetSuffix].filter(Boolean).join(' ') ||
      '',
    city: address.city || '',
    state: address.state || '',
    zipCode: address.postalCode || '',
    beds: property.bedrooms || 0,
    baths: Math.round(totalBaths * 10) / 10,
    sqft: property.area || 0,
    yearBuilt: property.yearBuilt || 0,
    propertyType: (property.type || '').toLowerCase(),
    status,
    mlsStatus: mlsInfo.status || '',
    daysOnMarket: mlsInfo.daysOnMarket || 0,
    lotSize: property.lotSizeSquareFeet || property.lotSizeArea || property.lotSize || 0,
    lotSizeAcres: property.lotSizeAcres || (property.lotSizeSquareFeet ? property.lotSizeSquareFeet / 43560 : 0),
    garageSpaces: property.garageSpaces || property.garage || 0,
    hoaFee: assoc.fee || assoc.associationFee || 0,
    hasPool: !!(property.pool || property.hasPool),
    hasSpa: !!(property.spa || property.hasSpa),
    hasView: !!(property.view || property.hasView),
    hasWaterfront: !!(property.waterfront || property.hasWaterfront),
    hasFireplace: !!(property.fireplace || (property.fireplaces > 0)),
    hasBasement: !!(property.basement || property.hasBasement),
    isSeniorCommunity: !!(property.seniorCommunity || property.isSeniorCommunity),
    isNewConstruction: !!(property.newConstruction || property.isNewConstruction),
    isFurnished: !!(property.furnished || property.isFurnished),
    hasOpenHouse: !!(mlsInfo.openHouse || mlsInfo.hasOpenHouse),
    hasVirtualTour: !!(mls.virtualTour || mlsInfo.virtualTour),
    photos: mls.photos || [],
    primaryPhoto: (mls.photos && mls.photos[0]) || '',
    description: mls.remarks || '',
    listingAgent: mls.agent ? {
      id: mls.agent.id,
      name: [mls.agent.firstName, mls.agent.lastName].filter(Boolean).join(' '),
      email: mls.agent.contact?.email,
      phone: mls.agent.contact?.cell || mls.agent.contact?.office,
      office: mls.office?.name,
    } : null,
  };
};

// ─── COMING SOON FLAG ───────────────────────────────────────────────────────
// Set to false once the Properties feature is approved for production.
const COMING_SOON = false;
// ────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 12; // Web loads 12 properties per page (mobile's SearchPropertyScreen uses 10 — different by design, not a bug)

// Filters persist for the browser session (cleared when the browser/tab is closed),
// but survive page reloads and navigating away from and back to this page.
const FILTERS_SESSION_KEY = 'ppg_saved_filters';

const loadSavedFilters = () => {
  try {
    const raw = sessionStorage.getItem(FILTERS_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.error('Error reading saved property filters:', error);
    return null;
  }
};

const saveFilters = (data) => {
  try {
    sessionStorage.setItem(FILTERS_SESSION_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving property filters:', error);
  }
};

const SORT_OPTIONS = [
  { label: 'Newest', value: 'newest' },
  { label: 'Price (low to high)', value: 'listprice' },
  { label: 'Price (high to low)', value: '-listprice' },
  { label: 'Square feet', value: '-sqft' },
  { label: 'Lot size', value: '-lotsize' },
  { label: 'Price/sq. ft.', value: '-pricesqft' },
];

const applyClientSort = (list, sort) => {
  const sorted = [...list];
  switch (sort) {
    case 'listprice':   return sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
    case '-listprice':  return sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
    case '-sqft':       return sorted.sort((a, b) => (b.sqft || 0) - (a.sqft || 0));
    case '-lotsize':    return sorted.sort((a, b) => (b.lotSize || 0) - (a.lotSize || 0));
    case '-pricesqft':  return sorted.sort((a, b) => {
      const aPsf = a.sqft ? (a.price || 0) / a.sqft : 0;
      const bPsf = b.sqft ? (b.price || 0) / b.sqft : 0;
      return bPsf - aPsf;
    });
    case 'newest':
    case '-listdate': // stale value from localStorage-persisted filters pre-dating the 'newest' rename
    default:
      // "Newest" is already the server's sort order for every request (including
      // Load More), and there's no reliable client-side field that reproduces it
      // (daysOnMarket doesn't track listdate closely enough) — re-sorting here
      // scrambles freshly-appended pages into the middle of the list instead of
      // leaving them after what's already shown. Preserve fetch order instead.
      return sorted;
  }
};


const PropertiesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Transaction-context param — lets a transaction's "Properties" tab deep-link into
  // this list (e.g. /properties?transactionId=xyz) so favoriting/viewing a property
  // here also marks it interested on that transaction, mirroring mobile's
  // SearchPropertyScreen.tsx route.params.transactionId. Absent by default, so
  // every other visit to /properties behaves exactly as before.
  const transactionId = new URLSearchParams(location.search).get('transactionId') || null;
  const [userRole, setUserRole] = useState('');
  const [searchQuery, setSearchQuery] = useState(() => loadSavedFilters()?.searchQuery || '');
  const [activeFilter, setActiveFilter] = useState(() => loadSavedFilters()?.activeFilter || 'all');
  const [rawProperties, setRawProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareProperty, setShareProperty] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [activeFilters, setActiveFilters] = useState(() => loadSavedFilters()?.activeFilters || DEFAULT_FILTERS);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // 'forSale' | 'price' | 'bedsBaths'
  const [dropdownDraft, setDropdownDraft] = useState({});
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [sortBy, setSortBy] = useState(() => loadSavedFilters()?.sortBy || 'newest');
  // Server-persisted favorite property IDs — hydrated once per page visit,
  // toggled optimistically (matches mobile favoritesSlice behavior).
  const [favoriteIds, setFavoriteIds] = useState(() => new Set());

  // Derived: client-side sort only — filters still go through the API
  const properties = useMemo(
    () => applyClientSort(rawProperties, sortBy),
    [rawProperties, sortBy]
  );

  // Persist filters for the browser session so they survive reloads/navigation
  // but are cleared once the browser (or tab) is closed.
  useEffect(() => {
    saveFilters({ searchQuery, activeFilter, activeFilters, sortBy });
  }, [searchQuery, activeFilter, activeFilters, sortBy]);

  // Detect user role from localStorage — always use the active/switched role
  useEffect(() => {
    try {
      const storedUserData = localStorage.getItem('user');
      const parsedData = storedUserData ? JSON.parse(storedUserData) : null;
      setUserRole(getActiveRole(parsedData));
    } catch (error) {
      console.error('Error parsing user data:', error);
      setUserRole('');
    }
  }, []);

  const isAgent = userRole === 'AGENT' || userRole === 'TRANSACTION_COORDINATOR';

  // Firebase token helper
  const getFirebaseToken = useCallback(() => {
    return new Promise((resolve, reject) => {
      // If user is already available, get token directly
      if (auth.currentUser) {
        auth.currentUser.getIdToken(true).then(resolve).catch(reject);
        return;
      }
      // Otherwise wait for auth state to be ready (with timeout)
      const timeoutId = setTimeout(() => {
        unsubscribe();
        reject(new Error('Firebase auth timeout'));
      }, 10000);
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        clearTimeout(timeoutId);
        unsubscribe();
        if (user) {
          try {
            const token = await user.getIdToken(true);
            resolve(token);
          } catch (error) {
            reject(error);
          }
        } else {
          reject(new Error('No authenticated user'));
        }
      });
    });
  }, []);

  // Fetch user's transactions for navigation context
  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const firebaseToken = await getFirebaseToken();
        const response = await apiClient.get(
          `${API_ENDPOINTS.transactions}?sortBy=createdAt&sortOrder=desc`,
          {
            headers: {
              'Authorization': `Bearer ${firebaseToken}`,
              'Content-Type': 'application/json'
            }
          }
        );

        const data = response.data;
        let txnList = [];
        if (Array.isArray(data)) txnList = data;
        else if (data && Array.isArray(data.content)) txnList = data.content;
        else if (data && Array.isArray(data.data)) txnList = data.data;

        setTransactions(txnList);
      } catch (error) {
        console.error('Error fetching transactions:', error);
      }
    };

    fetchTransactions();
  }, [getFirebaseToken]);

  // Hydrate the caller's favorited property IDs once per page visit so hearts
  // render correctly before any card-level toggle (mirrors mobile fetchFavoriteIds).
  useEffect(() => {
    const fetchFavoriteIds = async () => {
      try {
        const firebaseToken = await getFirebaseToken();
        const response = await apiClient.get(API_ENDPOINTS.favoritesIds, {
          headers: { 'Authorization': `Bearer ${firebaseToken}` }
        });
        const ids = response.data?.propertyIds ?? [];
        setFavoriteIds(new Set(ids.map(String)));
      } catch (error) {
        console.error('Error fetching favorite ids:', error);
      }
    };

    fetchFavoriteIds();
  }, [getFirebaseToken]);

  // MLS Search API - builds query params and calls /api/v1/mls/search
  // append=false → fresh search (replaces list); append=true → load more (extends list)
  const searchMlsProperties = useCallback(async (params = {}, append = false) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    try {
      const firebaseToken = await getFirebaseToken();

      const queryParams = new URLSearchParams();
      if (params.q) queryParams.append('q', params.q);
      if (params.city) queryParams.append('city', params.city);
      if (params.state) queryParams.append('state', params.state);
      if (params.postalCode) queryParams.append('postalCode', params.postalCode);
      if (params.minPrice !== undefined) queryParams.append('minPrice', String(params.minPrice));
      if (params.maxPrice !== undefined) queryParams.append('maxPrice', String(params.maxPrice));
      if (params.minBeds !== undefined) queryParams.append('minBeds', String(params.minBeds));
      if (params.maxBeds !== undefined) queryParams.append('maxBeds', String(params.maxBeds));
      if (params.minBaths !== undefined) queryParams.append('minBaths', String(params.minBaths));
      if (params.maxBaths !== undefined) queryParams.append('maxBaths', String(params.maxBaths));
      if (params.minArea !== undefined) queryParams.append('minArea', String(params.minArea));
      if (params.maxArea !== undefined) queryParams.append('maxArea', String(params.maxArea));
      if (params.type) queryParams.append('type', params.type);
      // Mirrors mobile's default MLS search call, which always sends status=Active
      // and sort=newest — an explicit advanced-filter status/sort still wins.
      queryParams.append('status', params.status || 'Active');
      queryParams.append('sort', params.sort || 'newest');
      queryParams.append('limit', String(PAGE_SIZE));
      if (params.offset > 0) queryParams.append('offset', String(params.offset));

      const url = `${API_ENDPOINTS.mlsSearch}?${queryParams.toString()}`;
      console.log('MLS Search URL:', url);

      const response = await apiClient.get(url, {
        headers: {
          'Authorization': `Bearer ${firebaseToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('MLS Search response:', response.data);

      const data = response.data;
      let propertiesList = [];
      // Mobile response format: { properties: [], count: N } — check this first
      if (data && Array.isArray(data.properties)) {
        propertiesList = data.properties;
      } else if (Array.isArray(data)) {
        propertiesList = data;
      } else if (data && Array.isArray(data.data)) {
        propertiesList = data.data;
      } else if (data && Array.isArray(data.results)) {
        propertiesList = data.results;
      } else if (data && Array.isArray(data.content)) {
        propertiesList = data.content;
      } else if (data && typeof data === 'object' && data.id) {
        propertiesList = [data];
      } else if (data && typeof data === 'object') {
        const arrayKey = Object.keys(data).find(key => Array.isArray(data[key]));
        if (arrayKey) propertiesList = data[arrayKey];
      }

      // Transform nested MLS format → flat UI format (same as mobile)
      const rawTransformed = propertiesList.map(p => transformMLSProperty(p)).filter(Boolean);
      if (rawTransformed.length > 0) {
        console.log('[Properties] First transformed property:', rawTransformed[0]);
      }

      // Reject listings with no real price. Mobile's smaller page size (10 vs
      // this page's 12) happens to reach the natural end of this same search's
      // real, priced results before its pagination window ever advances far
      // enough to hit this — the backend's federated multi-provider merge can
      // keep returning "full" pages well past that point that are actually
      // incomplete/placeholder records (no listPrice at all, hence the
      // "Price N/A" cards). Filtering them out here — instead of counting them
      // as real new results — lets Load More correctly recognize it's reached
      // the same end mobile does, rather than continuing to surface junk data.
      const transformed = rawTransformed.filter(p => typeof p.price === 'number' && p.price > 0);

      // Deduplicate at page boundary (matches mobile behaviour). Tracked outside
      // the updater so it's available below for the hasMore decision.
      let freshCount = transformed.length;
      if (append) {
        setRawProperties(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          const fresh = transformed.filter(p => !existingIds.has(p.id));
          freshCount = fresh.length;
          return [...prev, ...fresh];
        });
      } else {
        setRawProperties(transformed);
      }

      // `data.count`/`data.total`, if present, describe THIS page's item count
      // (same value as transformed.length) — not a grand total across the
      // whole search — so they can't be used as an early-stop signal. A
      // "Load More" page can come back at full PAGE_SIZE and still be 100%
      // properties already shown on an earlier page — the raw page size alone
      // says "there might be more," but if nothing on this page was actually
      // new, the result set is exhausted and clicking again would just
      // re-fetch duplicates. Require at least one fresh result in addition to
      // a full page before allowing another "Load More".
      setHasMore(transformed.length >= PAGE_SIZE && freshCount > 0);
    } catch (error) {
      console.error('Error searching MLS properties:', error);
      if (!append) setRawProperties([]);
      setHasMore(false);
    } finally {
      if (append) {
        setIsLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    }
  }, [getFirebaseToken]);

  // On mount: if the user already had filters/search set earlier this browser
  // session, restore that search instead of the profile-based default below.
  useEffect(() => {
    const hasRestoredSearch =
      !!(searchQuery && searchQuery.trim()) ||
      activeFilter !== 'all' ||
      countActiveFilters(activeFilters) > 0;

    if (hasRestoredSearch) {
      setHasMore(true);
      setHasSearched(true);
      searchMlsProperties({ ...buildAllParams(activeFilter, searchQuery, activeFilters), sort: sortBy }, false);
    }
    // Runs once on mount — searchQuery/activeFilter/activeFilters/sortBy here are
    // the values already restored (or defaulted) during initial state setup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On mount: auto-search using the user's profile ZIP (preferred) or state as fallback.
  // ZIP takes precedence; state is used only when ZIP is blank.
  // Skipped when a session-restored search/filter already took over above.
  useEffect(() => {
    const hasRestoredSearch =
      !!(searchQuery && searchQuery.trim()) ||
      activeFilter !== 'all' ||
      countActiveFilters(activeFilters) > 0;
    if (hasRestoredSearch) return;

    try {
      const storedUserData = localStorage.getItem('user');
      const parsedData = storedUserData ? JSON.parse(storedUserData) : null;
      if (!parsedData) return;

      const profileZip = (parsedData.postalCode || parsedData.zipCode || '').trim();
      const profileState = (parsedData.licenseState || parsedData.state || '').trim();

      if (profileZip) {
        setSearchQuery(profileZip);
        setHasMore(true);
        setHasSearched(true);
        searchMlsProperties({ postalCode: profileZip }, false);
      } else if (profileState) {
        // Normalise to 2-letter abbreviation for display; send full name to the API
        const stateAbbr = isUSStateAbbr(profileState)
          ? profileState.toUpperCase()
          : (Object.keys(STATE_NAMES).find(
              k => STATE_NAMES[k].toLowerCase() === profileState.toLowerCase()
            ) || profileState);
        setSearchQuery(stateAbbr);
        setHasMore(true);
        setHasSearched(true);
        searchMlsProperties({ state: toFullStateName(stateAbbr) || profileState }, false);
      }
    } catch (error) {
      console.error('Error reading profile for property auto-search:', error);
    }
    // Runs once on mount; only the guard above (evaluated against initial state) matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchMlsProperties]);

  // Build search params from active filter — matches mobile SearchPropertyScreen quick filters
  const getFilterParams = useCallback((filter, query) => {
    const params = {};

    // Route query: ZIP → postalCode, 2-letter state abbr → state full name,
    // digit-containing text → parsed street address (matches mobile), otherwise → city
    if (query && query.trim()) {
      const q = query.trim();
      if (/^\d{5}(-\d{4})?$/.test(q)) {
        params.postalCode = q;
      } else if (isUSStateAbbr(q)) {
        params.state = toFullStateName(q);
      } else if (/\d/.test(q)) {
        const parsed = parseFreeAddress(q);
        if (parsed.street) params.q = parsed.street;
        if (parsed.city) params.city = parsed.city;
        if (parsed.stateAbbr) params.state = toFullStateName(parsed.stateAbbr);
        if (parsed.zip) params.postalCode = parsed.zip;
      } else {
        params.city = q;
      }
    }

    // Filter params match mobile app's four quick-filter chips exactly
    switch (filter) {
      case 'under500k':
        params.maxPrice = 500000;
        break;
      case '3beds':
        params.minBeds = 3;
        break;
      case 'new':
        params.sort = 'newest';
        break;
      default: // 'all'
        break;
    }

    return params;
  }, []);

  // Merge chip-filter params + advanced filter params (advanced wins on conflict)
  const buildAllParams = useCallback((filterChip, query, advFilters) => {
    return { ...getFilterParams(filterChip, query), ...filtersToSearchParams(advFilters) };
  }, [getFilterParams]);

  // Handle search on Enter key — resets pagination and marks as searched
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      setHasMore(true);
      setHasSearched(true);
      searchMlsProperties({ ...buildAllParams(activeFilter, searchQuery, activeFilters), sort: sortBy }, false);
    }
  };

  // Filter click only updates the active filter — does NOT trigger a search.
  // Results are cleared so the view goes blank; user must re-search.
  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
    setRawProperties([]);
    setHasSearched(false);
  };

  // Load next page — offset based on raw (unfiltered) count to match server-side pagination.
  // Memoized (rather than a plain function) so the scroll-listener effect below can safely
  // depend on it without re-subscribing on every render.
  const handleLoadMore = useCallback(() => {
    if (isLoadingMore || isLoading || !hasMore) return;
    const params = { ...buildAllParams(activeFilter, searchQuery, activeFilters), sort: sortBy };
    params.offset = rawProperties.length;
    searchMlsProperties(params, true);
  }, [isLoadingMore, isLoading, hasMore, buildAllParams, activeFilter, searchQuery, activeFilters, sortBy, rawProperties.length, searchMlsProperties]);

  // Auto-load the next page on scroll-near-bottom — mirrors mobile's FlatList
  // onEndReached (SearchPropertyScreen.tsx). Calls the exact same handleLoadMore
  // used by the "Load More" button, so the button keeps working unchanged as a
  // visual fallback/indicator (matches mobile's renderFooter behavior).
  useEffect(() => {
    const handleScroll = () => {
      const scrolledNearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 600;
      if (scrolledNearBottom) handleLoadMore();
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleLoadMore]);

  // Called when user selects a suggestion from Google Places autocomplete.
  // Show the full selected address (not just city/state) so the search box
  // reflects what the user actually picked, matching the mobile app.
  const handleAddressSelect = ({ street, city, stateCode, zip, formattedAddress }) => {
    const displayText = formattedAddress
      ? formattedAddress.replace(/,\s*(USA|United States)\s*$/i, '')
      : [street, city, stateCode].filter(Boolean).join(', ');
    setSearchQuery(displayText);

    const params = { ...filtersToSearchParams(activeFilters) };
    // Apply chip filter flags (except text-based location ones which are replaced by the address)
    const chipParams = getFilterParams(activeFilter, '');
    Object.assign(params, chipParams);
    // Send the street as the keyword (q) and city/state/zip as their own params so the
    // search narrows to this specific property instead of every listing in the city.
    if (street) params.q = street;
    if (stateCode) params.state = toFullStateName(stateCode) || stateCode;
    if (city) params.city = city;
    if (zip) params.postalCode = zip;
    params.sort = sortBy;

    setHasMore(true);
    setHasSearched(true);
    searchMlsProperties(params, false);
  };

  // Called when user applies the advanced filter modal
  const handleApplyFilters = (newFilters) => {
    setActiveFilters(newFilters);
    setHasMore(true);
    setHasSearched(true);
    searchMlsProperties({ ...buildAllParams(activeFilter, searchQuery, newFilters), sort: sortBy }, false);
  };

  // Property field helpers — work on the flat transformed property object
  const safeString = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'object') return '';
    return String(val);
  };

  // Fields come from transformMLSProperty() — flat structure matches mobile
  const getPropertyAddress = (prop) => safeString(prop.address) || 'Unknown Address';
  const getPropertyMls = (prop) => safeString(prop.mlsId || prop.id || '');
  const getPropertyPrice = (prop) => {
    const price = prop.price || 0;
    if (!price) return 'Price N/A';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
  };
  const getPropertyDetails = (prop) => {
    // Match mobile format: "3 bed • 2 bath • 1,800 sqft" (singular like mobile)
    const beds = prop.beds || 0;
    const baths = prop.baths || 0;
    const sqft = prop.sqft || 0;
    const parts = [];
    if (beds) parts.push(`${beds} bed`);
    if (baths) parts.push(`${baths} bath`);
    if (sqft) parts.push(`${Number(sqft).toLocaleString()} sqft`);
    return parts.join(' \u2022 ') || 'Details N/A';
  };
  const getPropertyLocation = (prop) => {
    // Match mobile format: "City, State ZIP"
    const city = safeString(prop.city);
    const state = safeString(prop.state);
    const zip = safeString(prop.zipCode);
    const parts = [city, state].filter(Boolean).join(', ');
    return zip ? `${parts} ${zip}` : parts || 'Location N/A';
  };
  const getPropertyStatus = (prop) => safeString(prop.status) || 'ACTIVE';
  const getPropertyEmoji = (index) => {
    const emojis = ['\uD83C\uDFE0', '\uD83C\uDFE1', '\uD83C\uDFD8\uFE0F'];
    return emojis[index % emojis.length];
  };
  const getPropertyPhoto = (prop) => {
    if (!prop) return '';
    // After transform: primaryPhoto is the first photo string, photos is string[]
    if (prop.primaryPhoto) return prop.primaryPhoto;
    if (Array.isArray(prop.photos) && prop.photos.length > 0) {
      const first = prop.photos[0];
      return typeof first === 'string' ? first : (first?.url || first?.href || '');
    }
    return '';
  };
  const getPhotoCount = (prop) => (Array.isArray(prop?.photos) ? prop.photos.length : 0);

  const getBadgeClass = (status) => {
    switch ((status || '').toUpperCase()) {
      case 'NEW': return 'ppg-badge-new';
      case 'PENDING': return 'ppg-badge-pending';
      case 'OPEN_HOUSE': return 'ppg-badge-open-house';
      case 'PRICE_REDUCED': return 'ppg-badge-price-reduced';
      default: return 'ppg-badge-active'; // ACTIVE + fallback
    }
  };

  // Navigate to property detail — forwards transaction context (if present) into the
  // existing nested route so PropertyDetailPage picks up transactionId via its
  // current useParams() support; no changes needed on that page for this to work.
  const handleViewProperty = (prop) => {
    const mlsId = getPropertyMls(prop);
    if (mlsId) {
      navigate(transactionId ? `/transaction/${transactionId}/property/${mlsId}` : `/property/${mlsId}`);
    }
  };

  // Share property handler (agent only)
  const handleShareProperty = (prop) => {
    setShareProperty(prop);
    setShowShareModal(true);
  };

  // When favoriting a property while a transaction context is present, also mark it
  // interested on that transaction — mirrors mobile's SearchPropertyScreen.tsx
  // toggleFavorite() inline checkPropertyExists → addInterestedProperty side effect.
  // Best-effort: errors are swallowed (matches mobile) since the favorite itself
  // already succeeded and shouldn't be rolled back over this secondary action.
  const markInterestedFromFavorite = async (prop, firebaseToken) => {
    if (!transactionId) return;
    const mlsId = getPropertyMls(prop);
    if (!mlsId) return;

    try {
      const checkResponse = await apiClient.get(
        API_ENDPOINTS.checkInterestedProperty(transactionId, String(mlsId)),
        { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } }
      );
      if (checkResponse.data?.exists) return;

      await apiClient.post(
        API_ENDPOINTS.interestedProperties(transactionId),
        {
          mlsId: String(mlsId),
          propertyAddress: getPropertyAddress(prop),
          propertyCity: prop?.city || '',
          propertyState: prop?.state || '',
          propertyZip: prop?.zipCode || '',
          listPrice: prop?.price || 0,
          bedrooms: prop?.beds || undefined,
          bathrooms: prop?.baths || undefined,
          squareFeet: prop?.sqft || undefined,
          primaryPhoto: getPropertyPhoto(prop) || undefined,
          photos: Array.isArray(prop?.photos) && prop.photos.length > 0 ? prop.photos : undefined,
          propertyType: prop?.propertyType || undefined,
          yearBuilt: prop?.yearBuilt || undefined,
          lotSize: prop?.lotSize || undefined,
          description: prop?.description || undefined,
          daysOnMarket: prop?.daysOnMarket || undefined,
          hoaFee: prop?.hoaFee || undefined,
          garageSpaces: prop?.garageSpaces || undefined,
          lotSizeAcres: prop?.lotSizeAcres || undefined,
          listingAgentName: prop?.listingAgent?.name || undefined,
          listingAgentEmail: prop?.listingAgent?.email || undefined,
          listingAgentPhone: prop?.listingAgent?.phone || undefined,
          listingOfficeName: prop?.listingAgent?.office || undefined,
        },
        { headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' } }
      );
    } catch (error) {
      console.error('Error marking property as interested from favorite:', error);
    }
  };

  // Toggle favorite — optimistic flip with rollback on server failure
  // (mirrors mobile toggleFavoriteThunk). Stops propagation so the card's
  // own onClick (navigate to detail) doesn't also fire.
  const toggleFavorite = async (prop, e) => {
    if (e) e.stopPropagation();
    const propertyId = getPropertyMls(prop);
    if (!propertyId) return;
    const wasFavorite = favoriteIds.has(propertyId);

    setFavoriteIds(prev => {
      const next = new Set(prev);
      if (wasFavorite) next.delete(propertyId); else next.add(propertyId);
      return next;
    });

    try {
      const firebaseToken = await getFirebaseToken();
      if (wasFavorite) {
        await apiClient.delete(API_ENDPOINTS.favoriteProperty(propertyId), {
          headers: { 'Authorization': `Bearer ${firebaseToken}` }
        }).catch(err => {
          // Server 404 means it's already gone — treat as success (matches mobile removeFavorite)
          if (err?.response?.status !== 404) throw err;
        });
      } else {
        await apiClient.post(API_ENDPOINTS.favoriteProperty(propertyId), {}, {
          headers: { 'Authorization': `Bearer ${firebaseToken}` }
        });
        markInterestedFromFavorite(prop, firebaseToken);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      // Roll back to the persisted truth
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (wasFavorite) next.add(propertyId); else next.delete(propertyId);
        return next;
      });
    }
  };

  // ─── Dropdown filter helpers ────────────────────────────────────────────────

  const handleOpenDropdown = (key, btnEl) => {
    if (openDropdown === key) { setOpenDropdown(null); return; }
    const rect = btnEl.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 6, left: rect.left });
    const draft = { ...activeFilters };
    if (key === 'forSale' && !draft.status) draft.status = 'Active';
    setDropdownDraft(draft);
    setOpenDropdown(key);
  };

  const applyDropdownDraft = () => {
    setActiveFilters(dropdownDraft);
    setOpenDropdown(null);
    setHasMore(true);
    setHasSearched(true);
    searchMlsProperties({ ...buildAllParams(activeFilter, searchQuery, dropdownDraft), sort: sortBy }, false);
  };

  const resetDropdownFields = (fields) => {
    setDropdownDraft(prev => {
      const next = { ...prev };
      fields.forEach(f => { next[f] = DEFAULT_FILTERS[f]; });
      return next;
    });
  };

  const getForSaleLabel = () => {
    if (activeFilters.status === 'Pending') return 'Under Contract';
    if (activeFilters.status === 'Closed') return 'Sold';
    if (activeFilters.status === 'Rent') return 'For Rent';
    return 'For Sale';
  };

  const getPriceLabel = () => {
    const min = activeFilters.minPrice;
    const max = activeFilters.maxPrice;
    if (!min && !max) return 'Price';
    const fmt = (v) => {
      const n = parseInt(v, 10);
      if (n >= 1000000) return `$${(n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 1)}M`;
      if (n >= 1000) return `$${Math.round(n / 1000)}K`;
      return `$${n}`;
    };
    if (min && max) return `${fmt(min)} – ${fmt(max)}`;
    if (min) return `${fmt(min)}+`;
    return `Up to ${fmt(max)}`;
  };

  const getBedsBathsLabel = () => {
    const beds = activeFilters.beds;
    const baths = activeFilters.baths;
    const parts = [];
    if (beds && beds !== 'any') parts.push(beds === 'studio' ? 'Studio' : `${beds}+ bd`);
    if (baths && baths !== 'any') parts.push(`${baths}+ ba`);
    return parts.length > 0 ? parts.join(', ') : 'Beds/Baths';
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
  };

  const renderSortBar = () => (
    <div className="ppg-results-header">
      <span className="ppg-results-count">
        {properties.length} {properties.length === 1 ? 'result' : 'results'}
      </span>
      <div className="ppg-sort-wrap">
        <span className="ppg-sort-label">Sort:</span>
        <select
          className="ppg-sort-select"
          value={sortBy}
          onChange={(e) => handleSortChange(e.target.value)}
        >
          {SORT_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderFilterTabs = () => (
    <div className="ppg-filter-tabs">
      <div className="ppg-dd-wrap">
        <button
          className={`ppg-dd-btn${(activeFilters.status && activeFilters.status !== 'Active') ? ' ppg-dd-btn-active' : ''}`}
          onClick={(e) => handleOpenDropdown('forSale', e.currentTarget)}
        >
          <span>{getForSaleLabel()}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
      </div>
      <div className="ppg-dd-wrap">
        <button
          className={`ppg-dd-btn${(activeFilters.minPrice || activeFilters.maxPrice) ? ' ppg-dd-btn-active' : ''}`}
          onClick={(e) => handleOpenDropdown('price', e.currentTarget)}
        >
          <span>{getPriceLabel()}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
      </div>
      <div className="ppg-dd-wrap">
        <button
          className={`ppg-dd-btn${(activeFilters.beds !== 'any' || activeFilters.baths !== 'any') ? ' ppg-dd-btn-active' : ''}`}
          onClick={(e) => handleOpenDropdown('bedsBaths', e.currentTarget)}
        >
          <span>{getBedsBathsLabel()}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
      </div>
      <button
        className={`ppg-filters-btn${countActiveFilters(activeFilters) > 0 ? ' ppg-filters-btn-active' : ''}`}
        onClick={() => setShowFiltersModal(true)}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/>
        </svg>
        Filters{countActiveFilters(activeFilters) > 0 ? ` • ${countActiveFilters(activeFilters)}` : ''}
      </button>
    </div>
  );

  // ============================================
  // AGENT VIEW
  // ============================================
  const renderAgentView = () => (
    <div className="ppg-content">
      {/* Header */}
      <div className="ppg-content-header">
        <h1 className="ppg-page-title">Property Search</h1>
        <div className="ppg-header-actions">
          <button className="ppg-favorites-btn" onClick={() => navigate('/favorites')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
            <span>Favorites</span>
          </button>
        </div>
      </div>

      {/* Search Section */}
      <div className="ppg-search-section">
        <div className="ppg-search-wrapper">
          <span className="ppg-search-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </span>
          <AddressAutocomplete
            value={searchQuery}
            onChange={setSearchQuery}
            onAddressSelect={handleAddressSelect}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search by address, city, or ZIP code..."
            inputClassName="ppg-search-input"
          />
        </div>
        {renderFilterTabs()}
      </div>

      {/* Properties Grid */}
      {isLoading ? (
        <div className="ppg-loading">
          <div className="ppg-loading-spinner"></div>
          <span>Searching properties...</span>
        </div>
      ) : properties.length > 0 ? (
        <>
          {renderSortBar()}
          <div className="ppg-grid">
            {properties.map((prop, index) => {
              const status = getPropertyStatus(prop);
              const photoCount = getPhotoCount(prop);
              const dom = prop.daysOnMarket || 0;
              return (
                <div className="ppg-card" key={prop.id || prop.mlsId || index} onClick={() => handleViewProperty(prop)}>
                  <div className="ppg-card-image">
                    {getPropertyPhoto(prop) ? (
                      <img src={getPropertyPhoto(prop)} alt={getPropertyAddress(prop)} className="ppg-card-img" />
                    ) : (
                      <div className="ppg-card-img-placeholder">{getPropertyEmoji(index)}</div>
                    )}
                    {/* Status badge — top left */}
                    {status && (
                      <div className={`ppg-card-badge ${getBadgeClass(status)}`}>
                        {status.toUpperCase()}
                      </div>
                    )}
                    {/* Favorite heart — top right */}
                    <div
                      className={`ppg-saved-icon${favoriteIds.has(getPropertyMls(prop)) ? ' liked' : ''}`}
                      onClick={(e) => toggleFavorite(prop, e)}
                      role="button"
                      aria-label={favoriteIds.has(getPropertyMls(prop)) ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      {favoriteIds.has(getPropertyMls(prop)) ? '♥' : '♡'}
                    </div>
                    {/* Photo count — bottom left */}
                    {photoCount > 0 && (
                      <div className="ppg-photo-count">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg> {photoCount}
                      </div>
                    )}
                    {/* Days on market — bottom right */}
                    {dom > 0 && (
                      <div className="ppg-dom-badge">{dom} days</div>
                    )}
                  </div>
                  <div className="ppg-card-info">
                    {/* Price first — matches mobile */}
                    <div className="ppg-card-price">{getPropertyPrice(prop)}</div>
                    <div className="ppg-card-address">{getPropertyAddress(prop)}</div>
                    <div className="ppg-card-location">{getPropertyLocation(prop)}</div>

                    {/* Specs row — matches mobile: "3 bed • 2 bath • 1,800 sqft" */}
                    <div className="ppg-card-specs">
                      {getPropertyDetails(prop)}
                    </div>

                    <div className="ppg-card-actions">
                      <button
                        className="ppg-action-btn ppg-action-btn-secondary"
                        onClick={(e) => { e.stopPropagation(); handleShareProperty(prop); }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                        <span>Share</span>
                      </button>
                      <button
                        className="ppg-action-btn ppg-action-btn-primary"
                        onClick={(e) => { e.stopPropagation(); handleViewProperty(prop); }}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="ppg-load-more">
            {isLoadingMore ? (
              <div className="ppg-loading-more">
                <div className="ppg-loading-spinner-sm"></div>
                <span>Loading more...</span>
              </div>
            ) : hasMore ? (
              <button className="ppg-load-more-btn" onClick={handleLoadMore}>Load More</button>
            ) : (
              <div className="ppg-no-more">No more results</div>
            )}
          </div>
        </>
      ) : (
        <div className="ppg-empty">
          <div className="ppg-empty-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#C7C7CC" strokeWidth="1.5">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </div>
          {hasSearched ? (
            <>
              <h3>No Properties Found</h3>
              <p>Try a different search term or adjust your filters.</p>
            </>
          ) : (
            <>
              <h3>Search MLS Properties</h3>
              <p>Enter an address, city, or ZIP code above and press Enter to find properties.</p>
            </>
          )}
        </div>
      )}
    </div>
  );

  // ============================================
  // BUYER VIEW
  // ============================================
  const renderBuyerView = () => (
    <div className="ppg-content">
      {/* Header */}
      <div className="ppg-content-header">
        <h1 className="ppg-page-title">Find Your Dream Home</h1>
        <div className="ppg-header-actions">
          <button className="ppg-favorites-btn" onClick={() => navigate('/favorites')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
            <span>Favorites</span>
          </button>
        </div>
      </div>

      {/* Search Section */}
      <div className="ppg-search-section">
        <div className="ppg-search-row">
          <div className="ppg-search-wrapper">
            <span className="ppg-search-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <AddressAutocomplete
              value={searchQuery}
              onChange={setSearchQuery}
              onAddressSelect={handleAddressSelect}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search by address, city, or ZIP code..."
              inputClassName="ppg-search-input"
            />
          </div>
          <button
            className="ppg-search-btn"
            onClick={() => {
              setHasMore(true);
              setHasSearched(true);
              searchMlsProperties({ ...buildAllParams(activeFilter, searchQuery, activeFilters), sort: sortBy }, false);
            }}
          >
            Search
          </button>
        </div>
        {renderFilterTabs()}
      </div>

      {/* Quick Access */}
      <div className="ppg-quick-access">
        <div className="ppg-quick-access-title">Quick Access</div>
        <div className="ppg-quick-access-items">
          <div className="ppg-quick-item" onClick={() => navigate('/favorites')}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> My Favorites</div>
          <div className="ppg-quick-item"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> From My Agent</div>
          <div className="ppg-quick-item"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> Recently Viewed</div>
          <div className="ppg-quick-item"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> Tours Scheduled</div>
        </div>
      </div>

      {/* Properties Grid */}
      {isLoading ? (
        <div className="ppg-loading">
          <div className="ppg-loading-spinner"></div>
          <span>Searching properties...</span>
        </div>
      ) : properties.length > 0 ? (
        <>
          {renderSortBar()}
          <div className="ppg-grid">
            {properties.map((prop, index) => {
              const status = getPropertyStatus(prop);
              const photoCount = getPhotoCount(prop);
              const dom = prop.daysOnMarket || 0;
              return (
                <div className="ppg-card" key={prop.id || prop.mlsId || index} onClick={() => handleViewProperty(prop)}>
                  <div className="ppg-card-image">
                    {getPropertyPhoto(prop) ? (
                      <img src={getPropertyPhoto(prop)} alt={getPropertyAddress(prop)} className="ppg-card-img" />
                    ) : (
                      <div className="ppg-card-img-placeholder">{getPropertyEmoji(index)}</div>
                    )}
                    {/* Status badge — top left */}
                    {status && (
                      <div className={`ppg-card-badge ${getBadgeClass(status)}`}>
                        {status.toUpperCase()}
                      </div>
                    )}
                    {/* Favorite heart — top right */}
                    <div
                      className={`ppg-saved-icon${favoriteIds.has(getPropertyMls(prop)) ? ' liked' : ''}`}
                      onClick={(e) => toggleFavorite(prop, e)}
                      role="button"
                      aria-label={favoriteIds.has(getPropertyMls(prop)) ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      {favoriteIds.has(getPropertyMls(prop)) ? '♥' : '♡'}
                    </div>
                    {/* Photo count — bottom left */}
                    {photoCount > 0 && (
                      <div className="ppg-photo-count">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg> {photoCount}
                      </div>
                    )}
                    {/* Days on market — bottom right */}
                    {dom > 0 && (
                      <div className="ppg-dom-badge">{dom} days</div>
                    )}
                  </div>
                  <div className="ppg-card-info">
                    {/* Price first — matches mobile */}
                    <div className="ppg-card-price">{getPropertyPrice(prop)}</div>
                    <div className="ppg-card-address">{getPropertyAddress(prop)}</div>
                    <div className="ppg-card-location">{getPropertyLocation(prop)}</div>

                    {/* Specs row — matches mobile: "3 bed • 2 bath • 1,800 sqft" */}
                    <div className="ppg-card-specs">
                      {getPropertyDetails(prop)}
                    </div>

                    <div className="ppg-card-actions">
                      <button
                        className="ppg-action-btn ppg-action-btn-primary"
                        onClick={(e) => { e.stopPropagation(); handleViewProperty(prop); }}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="ppg-load-more">
            {isLoadingMore ? (
              <div className="ppg-loading-more">
                <div className="ppg-loading-spinner-sm"></div>
                <span>Loading more...</span>
              </div>
            ) : hasMore ? (
              <button className="ppg-load-more-btn" onClick={handleLoadMore}>Load More</button>
            ) : (
              <div className="ppg-no-more">No more results</div>
            )}
          </div>
        </>
      ) : (
        <div className="ppg-empty">
          <div className="ppg-empty-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#C7C7CC" strokeWidth="1.5">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </div>
          {hasSearched ? (
            <>
              <h3>No Properties Found</h3>
              <p>Try a different search term or adjust your filters.</p>
            </>
          ) : (
            <>
              <h3>Find Your Dream Home</h3>
              <p>Enter an address, neighborhood, or ZIP code above to search for properties.</p>
            </>
          )}
        </div>
      )}
    </div>
  );

  // ============================================
  // COMING SOON VIEW
  // ============================================
  const renderComingSoonView = () => (
    <div className="ppg-content ppg-cs-content">
      {/* Page header */}
      <div className="ppg-web-page-hdr">
        <div className="ppg-page-title">Properties</div>
        <div className="ppg-web-page-sub">Search and discover properties</div>
      </div>

      {/* Coming Soon Banner */}
      <div className="ppg-cs-banner">
        <div className="ppg-cs-icon-wrap">
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 18L20 5L35 18V36C35 36.5523 34.5523 37 34 37H25V27H15V37H6C5.44772 37 5 36.5523 5 36V18Z" fill="#5B47D6" />
            <path d="M5 18L20 5L35 18" stroke="#2D1B69" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <rect x="15" y="27" width="10" height="10" fill="#E8E0FF" />
            <rect x="11" y="20" width="7" height="7" rx="1" fill="white" fillOpacity="0.85" />
            <rect x="22" y="20" width="7" height="7" rx="1" fill="white" fillOpacity="0.85" />
          </svg>
        </div>
        <div className="ppg-cs-badge">Coming Soon</div>
        <div className="ppg-cs-title">Property Search &amp; Discovery</div>
        <div className="ppg-cs-msg">
          We&rsquo;re actively working on partnerships to enable property search and discovery. Stay tuned!
        </div>
      </div>

    </div>
  );

  return (
    <div className="ppg-container">
      <div className="ppg-layout">
        {COMING_SOON ? renderComingSoonView() : (isAgent ? renderAgentView() : renderBuyerView())}
      </div>

      {/* Dropdown overlay — closes any open dropdown on outside click */}
      {openDropdown && <div className="ppg-dd-overlay" onClick={() => setOpenDropdown(null)} />}

      {/* For Sale dropdown panel */}
      {openDropdown === 'forSale' && (
        <div className="ppg-dd-panel" style={{ top: dropdownPos.top, left: dropdownPos.left }} onClick={e => e.stopPropagation()}>
          <div className="ppg-dd-body">
            {[
              { label: 'For Sale', value: 'Active', sub: 'Active listings' },
              { label: 'Under Contract / Pending', value: 'Pending', sub: null },
              { label: 'Sold', value: 'Closed', sub: null },
              { label: 'Any Status', value: '', sub: null },
              { label: 'For Rent', value: 'Rent', sub: null },
            ].map(opt => (
              <div
                key={opt.value || 'any'}
                className="ppg-dd-option"
                onClick={() => setDropdownDraft(prev => ({ ...prev, status: opt.value }))}
              >
                <span className={`ppg-dd-radio${dropdownDraft.status === opt.value ? ' ppg-dd-radio-checked' : ''}`} />
                <div>
                  <div className="ppg-dd-option-label">{opt.label}</div>
                  {opt.sub && <div className="ppg-dd-option-sub">{opt.sub}</div>}
                </div>
              </div>
            ))}
          </div>
          <div className="ppg-dd-footer">
            <button className="ppg-dd-reset" onClick={() => resetDropdownFields(['status'])}>Reset</button>
            <button className="ppg-dd-done" onClick={applyDropdownDraft}>Done</button>
          </div>
        </div>
      )}

      {/* Price dropdown panel */}
      {openDropdown === 'price' && (
        <div className="ppg-dd-panel ppg-dd-panel-price" style={{ top: dropdownPos.top, left: dropdownPos.left }} onClick={e => e.stopPropagation()}>
          <div className="ppg-dd-body">
            <div className="ppg-dd-section-title">Price range</div>
            <div className="ppg-dd-price-row">
              <div className="ppg-dd-price-wrap">
                <span className="ppg-dd-price-prefix">$</span>
                <input
                  type="number"
                  className="ppg-dd-price-input"
                  placeholder="No min"
                  value={dropdownDraft.minPrice || ''}
                  min={0}
                  onChange={e => setDropdownDraft(prev => ({ ...prev, minPrice: e.target.value }))}
                />
              </div>
              <span className="ppg-dd-price-dash">–</span>
              <div className="ppg-dd-price-wrap">
                <span className="ppg-dd-price-prefix">$</span>
                <input
                  type="number"
                  className="ppg-dd-price-input"
                  placeholder="No max"
                  value={dropdownDraft.maxPrice || ''}
                  min={0}
                  onChange={e => setDropdownDraft(prev => ({ ...prev, maxPrice: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <div className="ppg-dd-footer">
            <button className="ppg-dd-reset" onClick={() => resetDropdownFields(['minPrice', 'maxPrice'])}>Reset</button>
            <button className="ppg-dd-done" onClick={applyDropdownDraft}>Done</button>
          </div>
        </div>
      )}

      {/* Beds/Baths dropdown panel */}
      {openDropdown === 'bedsBaths' && (
        <div className="ppg-dd-panel ppg-dd-panel-wide" style={{ top: dropdownPos.top, left: dropdownPos.left }} onClick={e => e.stopPropagation()}>
          <div className="ppg-dd-body">
            <div className="ppg-dd-section-title">Beds</div>
            <div className="ppg-dd-pill-row">
              {['Any', 'Studio', '1', '2', '3', '4', '5+'].map(opt => {
                const val = opt.toLowerCase() === 'any' ? 'any' : opt.toLowerCase();
                return (
                  <button
                    key={opt}
                    className={`ppg-dd-pill${dropdownDraft.beds === val ? ' ppg-dd-pill-active' : ''}`}
                    onClick={() => setDropdownDraft(prev => ({ ...prev, beds: val }))}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
            <div className="ppg-dd-section-title" style={{ marginTop: 18 }}>Baths</div>
            <div className="ppg-dd-pill-row">
              {[
                { label: 'Any', value: 'any' },
                { label: '1+', value: '1' },
                { label: '1.5+', value: '1.5' },
                { label: '2+', value: '2' },
                { label: '2.5+', value: '2.5' },
                { label: '3+', value: '3' },
                { label: '4+', value: '4' },
              ].map(opt => (
                <button
                  key={opt.value}
                  className={`ppg-dd-pill${dropdownDraft.baths === opt.value ? ' ppg-dd-pill-active' : ''}`}
                  onClick={() => setDropdownDraft(prev => ({ ...prev, baths: opt.value }))}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="ppg-dd-footer">
            <button className="ppg-dd-reset" onClick={() => resetDropdownFields(['beds', 'baths'])}>Reset</button>
            <button className="ppg-dd-done" onClick={applyDropdownDraft}>Done</button>
          </div>
        </div>
      )}

      {/* Advanced Filters Modal */}
      <PropertyFiltersModal
        isOpen={showFiltersModal}
        onClose={() => setShowFiltersModal(false)}
        initialFilters={activeFilters}
        onApply={handleApplyFilters}
      />

      {/* Share Property Modal */}
      <SharePropertyModal
        isOpen={showShareModal}
        onClose={() => { setShowShareModal(false); setShareProperty(null); }}
        property={shareProperty}
        getPropertyAddress={getPropertyAddress}
        getPropertyPrice={getPropertyPrice}
        getPropertyDetails={getPropertyDetails}
        getPropertyPhoto={getPropertyPhoto}
        getPropertyMls={getPropertyMls}
      />
    </div>
  );
};

export default PropertiesPage;
